import type { Dispatch } from 'react';
import type { UserLocalStorage } from '../../types/localStorage';
import type {
  ApiRequestObject,
  BaseApiRequest,
  BaseApiResponse,
  BaseBatchApiRequest,
  BaseUnauthenticatedApiRequest,
} from './types/api-request';

import { getModal } from '../../contexts/ModalContext';

import { WarningTriangleRedIcon } from '../../images/shapes';

import LoginModalForm from '../../form/LoginModalForm/LoginModalForm';
import i18next from '../../i18n';
import { getEnv } from '../env';
import { removeTrailingSlash } from '../helpers';
import { refreshToken } from './auth-requests';
import { fetchLogin } from './fetch-login';

class RefreshStatus {
  newJwt: string;
  isRefreshing: boolean;
  isRefreshed: boolean;
  isInvalid: boolean;
  fetchRequest: Promise<any> | null;
  loginRequest: Promise<any> | null;

  constructor() {
    this.newJwt = '';
    this.isRefreshing = false;
    this.isRefreshed = false;
    this.isInvalid = false;
    this.fetchRequest = null;
    this.loginRequest = null;
  }

  reset() {
    this.isRefreshing = false;
    this.isRefreshed = false;
    this.isInvalid = false;
    this.fetchRequest = null;
    this.loginRequest = null;
  }
}

const refreshStatus = new RefreshStatus();

export const resetRefreshStatus = () =>
  Object.assign(refreshStatus, new RefreshStatus());

const refreshTokenCallback = async () => {
  try {
    return await refreshToken();
  } catch {
    window.location.replace('/logout?reason=auth-error');
  }
};

export class MissingParamError extends Error {
  constructor(url: string) {
    super(`Not all params are provided to url: ${url}`);
  }
}

const showLoginModal = async () => {
  const email = JSON.parse(localStorage.getItem('cms.user') || '{}')?.data
    ?.email;
  const modal = getModal();
  refreshStatus.newJwt = await modal({
    title: (
      <div className="inline-flex text-red font-bold text-3xl items-center">
        <WarningTriangleRedIcon className="h-5 mr-2.5" />
        {i18next.t('Global.SessionExpired')}
      </div>
    ),
    content: (
      <LoginModalForm
        email={email}
        onSubmit={(
          values: { password: string },
          setUser: Dispatch<UserLocalStorage>,
        ) =>
          fetchLogin(
            { password: values?.password || '', email },
            setUser,
            i18next.t,
          )
        }
      />
    ),
    hideClose: true,
  });
};

/**
 * Creates fetch method
 */
export const fetchMethod = (
  url: string,
  method?: string,
  jwt?: string,
  space?: string,
  options: ApiRequestObject = null,
) =>
  fetch(url, {
    method,
    ...options,
    headers: {
      Accept: 'application/json',
      ...(options?.public ? {} : { 'X-MODE': 'preview' }),
      ...(jwt ? { Authorization: `JWT ${jwt}` } : {}),
      ...(options?.skipContentType
        ? {}
        : { 'Content-Type': 'application/json; charset=UTF-8' }),
      ...(space ? { 'X-SPACE-ID': space } : {}),
      ...(options?.headers ?? {}),
    },
  });

export async function apiRequest(
  method: string,
  path: string,
  jwt?: string,
  space?: string,
  options: ApiRequestObject = null,
) {
  const baseUrl = getEnv('VITE_FLOTIQ_API_URL') || '';
  const url = `${removeTrailingSlash(baseUrl)}/${path.replace(/^\/*/, '')}`;

  const impersonateString = sessionStorage.getItem('cms.impersonate');
  const impersonate = impersonateString ? JSON.parse(impersonateString) : null;
  const requestOptions = {
    ...(options ?? {}),
    ...(impersonate
      ? {
          headers: {
            'X-SWITCH-USER': impersonate.email,
          },
        }
      : null),
  };

  const response = await fetchMethod(url, method, jwt, space, requestOptions);

  if (response?.status === 401 && jwt) {
    if (!refreshStatus.isRefreshing) {
      refreshStatus.isRefreshing = true;

      refreshStatus.fetchRequest = refreshTokenCallback();
      const refreshResponse = await refreshStatus.fetchRequest;

      if (!refreshResponse) {
        //if there is no refresh response do nothing and return request
      } else if (refreshResponse.ok) {
        localStorage.setItem('cms.user', JSON.stringify(refreshResponse.body));

        window.dispatchEvent(
          new StorageEvent('storage', {
            storageArea: window.localStorage,
            url: window.location.href,
            key: 'cms.user',
          }),
        );

        refreshStatus.newJwt = refreshResponse.body.token;
      } else {
        refreshStatus.isInvalid = true;
        refreshStatus.loginRequest = showLoginModal();
      }
      refreshStatus.isRefreshed = true;
    } else {
      await refreshStatus.fetchRequest;
    }

    await refreshStatus.loginRequest;
    refreshStatus.reset();

    return fetchMethod(
      url,
      method,
      refreshStatus.newJwt,
      space,
      requestOptions,
    );
  }

  return response;
}

/**
 * Applies url params to the url. Returns url with substituted values, e.g. api/data/123
 */
const applyUrlParams = (url: string, params: ApiRequestObject) => {
  if (!params) return url;

  const newUrl = Object.keys(params).reduce((url, key) => {
    const newUrl = url.replaceAll(
      `{{${key}}}`,
      encodeURIComponent(params[key]),
    );
    if (newUrl !== url) delete params[key];

    return newUrl;
  }, url);

  if (/{{[a-z0-9]+}}/i.test(newUrl)) throw new MissingParamError(url);

  return newUrl;
};

/**
 * Appends url params to the url. Returns url combined with query params
 */
export const appendQueryParams = (url: string, params: ApiRequestObject) => {
  if (!params) return url;

  const keyValues: string[] = [];
  Object.keys(params).forEach((key) => {
    if (params[key] != null) {
      const encodedKey = encodeURIComponent(key);
      if (Array.isArray(params[key])) {
        const arrayString = params[key]
          .map((el) => `${encodedKey}[]=${encodeURIComponent(el)}`)
          .join('&');
        keyValues.push(arrayString);
      } else {
        keyValues.push(`${encodedKey}=${encodeURIComponent(params[key])}`);
      }
    }
  });
  const queryString = keyValues.join('&');
  if (!queryString) return url;
  if (url.includes('?')) {
    return `${url}&${queryString}`;
  } else {
    return `${url}?${queryString}`;
  }
};

/**
 * Parses response into a valid object depending on response content-type
 */
export async function defaultResponseParser<
  T extends BaseApiResponse<any> | Response,
>(res: Response) {
  const headers = Object.fromEntries(res.headers.entries());
  if (headers['content-type']?.includes('application/json'))
    return {
      status: res.status,
      body: await res.json(),
      ok: res.ok,
      retryAfter: headers['retry-after']
        ? parseInt(headers['retry-after'])
        : null,
    } as T;
  return res as T;
}

/**
 * Creates a method for querying a listing url. Used for GET, DELETE and HEAD queries
 */
export function makeBodilessQuery<T extends BaseApiResponse<any>>(
  name: string,
  method: string = 'GET',
  defaultParams: Record<string, any> = {},
  defaultOptions: ApiRequestObject = {},
): BaseApiRequest<T> {
  return (
    jwt?: string,
    space?: string,
    params: ApiRequestObject = defaultParams,
    options: ApiRequestObject = defaultOptions,
  ) => {
    let url = `api/${name}`;
    const paramsLocalCopy = JSON.parse(JSON.stringify(params || {}));
    url = applyUrlParams(url, paramsLocalCopy);
    url = appendQueryParams(url, { ...defaultParams, ...paramsLocalCopy });
    return apiRequest(method, url, jwt, space, options).then(
      defaultResponseParser<T>,
    );
  };
}

const buildUrlAndBody = (
  name: string,
  body: ApiRequestObject,
  otherParams: ApiRequestObject = null,
) => {
  let url = `api/${name}`;
  const bodyLocalCopy = Array.isArray(body)
    ? Object.assign({}, body)
    : JSON.parse(JSON.stringify(body));

  url = applyUrlParams(url, otherParams || bodyLocalCopy);
  return { url, bodyLocalCopy };
};

/**
 * Creates JSON query function. Used for POST, PUT, and PATCH queries
 */
export function makeJSONQuery<T extends BaseApiResponse<any>>(
  name: string,
  method: string = 'POST',
): BaseApiRequest<T> {
  return (
    jwt?: string,
    space?: string,
    body: ApiRequestObject = {},
    options: ApiRequestObject = {},
  ) => {
    const { url, bodyLocalCopy } = buildUrlAndBody(name, body);
    return apiRequest(method, url, jwt, space, {
      body: JSON.stringify(bodyLocalCopy),
      ...options,
    }).then(defaultResponseParser<T>);
  };
}

/**
 * Creates JSON query function. Used for POST, PUT, and PATCH queries
 */
export function makeBatchJSONQuery<T extends BaseApiResponse<any>>(
  name: string,
  method: string = 'POST',
): BaseBatchApiRequest<T> {
  return (
    jwt?: string,
    space?: string,
    contentTypeName?: string,
    body: ApiRequestObject = {},
    options: ApiRequestObject = {},
  ) => {
    const { url, bodyLocalCopy } = buildUrlAndBody(name, body, {
      contentTypeName,
    });
    return apiRequest(method, url, jwt, space, {
      body: JSON.stringify(bodyLocalCopy),
      ...options,
    }).then(defaultResponseParser<T>);
  };
}

/**
 * Creates JSON query function without authentication. Used for POST, PUT, and PATCH queries
 */
export function makeUnauthenticatedJSONQuery<T extends BaseApiResponse<any>>(
  name: string,
  method: string = 'POST',
): BaseUnauthenticatedApiRequest<T> {
  return (body: ApiRequestObject = {}, options: ApiRequestObject = {}) => {
    const { url, bodyLocalCopy } = buildUrlAndBody(name, body);
    return apiRequest(method, url, undefined, undefined, {
      body: JSON.stringify(bodyLocalCopy),
      ...options,
    }).then(defaultResponseParser<T>);
  };
}

/**
 * Creates bodiless query function for external services. Used for GET, DELETE and HEAD queries
 */
export function makeExternalServiceQuery<T extends BaseApiResponse<any>>(
  url: string,
  method = 'GET',
  requestOptions: ApiRequestObject = {},
  defaultParams: ApiRequestObject = {},
  withJwt = false,
): BaseApiRequest<T> {
  return (
    jwt?: string,
    _space?: string,
    params: ApiRequestObject = defaultParams,
    options: ApiRequestObject = {},
  ) => {
    const paramsLocalCopy = JSON.parse(JSON.stringify(params || {}));
    let urlWithParams = applyUrlParams(url, paramsLocalCopy);
    urlWithParams = appendQueryParams(urlWithParams, {
      ...defaultParams,
      ...paramsLocalCopy,
    });

    const finalOptions = { ...requestOptions, ...options };

    return fetchMethod(
      urlWithParams,
      method,
      withJwt ? jwt : undefined,
      undefined,
      finalOptions,
    ).then(defaultResponseParser<T>);
  };
}
